import * as React from "react"
import { SlidersHorizontal, Plus, X } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"

import {
  OPERATORS_BY_TYPE,
  countActiveRules,
  type FilterColumn,
  type FilterConjunction,
  type FilterRule,
} from "./filters"

export interface FilterBuilderProps<T = unknown> {
  columns: FilterColumn<T>[]
  rules: FilterRule[]
  conjunction: FilterConjunction
  onRulesChange: (rules: FilterRule[]) => void
  onConjunctionChange: (conjunction: FilterConjunction) => void
  className?: string
}

function makeRuleId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }
  return `rule-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

/**
 * monday.com-style condition-filter panel: a toolbar "Filters" button with an
 * active-rule-count badge that opens a popover of rule rows
 * ([Where|And/Or] [column] [operator] [value] [×]). Fully controlled —
 * filtering is applied live by the parent via `applyFilterRules`.
 */
export function FilterBuilder<T = unknown>({
  columns,
  rules,
  conjunction,
  onRulesChange,
  onConjunctionChange,
  className,
}: FilterBuilderProps<T>) {
  const activeCount = countActiveRules(rules, columns)

  const addRule = () => {
    const column = columns[0]
    if (!column) return
    onRulesChange([
      ...rules,
      {
        id: makeRuleId(),
        columnKey: column.key,
        operator: OPERATORS_BY_TYPE[column.type][0].value,
        value: "",
      },
    ])
  }

  const removeRule = (id: string) => {
    onRulesChange(rules.filter((rule) => rule.id !== id))
  }

  const updateRule = (id: string, patch: Partial<FilterRule>) => {
    onRulesChange(
      rules.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule))
    )
  }

  /** Column switch resets operator (per new type) and clears the value. */
  const changeColumn = (id: string, columnKey: string) => {
    const column = columns.find((c) => c.key === columnKey)
    if (!column) return
    updateRule(id, {
      columnKey,
      operator: OPERATORS_BY_TYPE[column.type][0].value,
      value: "",
    })
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn("gap-2", className)}>
          <SlidersHorizontal className="h-4 w-4" />
          Filters
          {activeCount > 0 && (
            <Badge
              variant="outline"
              className="border-coral/30 bg-coral-light px-1.5 text-coral-dark"
            >
              {activeCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto max-w-[95vw] p-3">
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm font-medium">Filters</div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground"
            disabled={!rules.length}
            onClick={() => onRulesChange([])}
          >
            Clear all
          </Button>
        </div>

        {rules.length ? (
          <div className="mt-2 space-y-2">
            {rules.map((rule, index) => {
              const column = columns.find((c) => c.key === rule.columnKey)
              const operators = column ? OPERATORS_BY_TYPE[column.type] : []
              return (
                <div key={rule.id} className="flex items-center gap-2">
                  {index === 0 ? (
                    <span className="w-[72px] flex-none pl-1 text-sm text-muted-foreground">
                      Where
                    </span>
                  ) : (
                    <Select
                      value={conjunction}
                      onValueChange={(value) =>
                        onConjunctionChange(value as FilterConjunction)
                      }
                    >
                      <SelectTrigger className="h-8 w-[72px] flex-none text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="and">And</SelectItem>
                        <SelectItem value="or">Or</SelectItem>
                      </SelectContent>
                    </Select>
                  )}

                  <Select
                    value={rule.columnKey}
                    onValueChange={(value) => changeColumn(rule.id, value)}
                  >
                    <SelectTrigger className="h-8 w-[170px] text-sm">
                      <SelectValue placeholder="Column" />
                    </SelectTrigger>
                    <SelectContent>
                      {columns.map((col) => (
                        <SelectItem key={col.key} value={col.key}>
                          {col.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={rule.operator}
                    onValueChange={(value) =>
                      updateRule(rule.id, { operator: value })
                    }
                  >
                    <SelectTrigger className="h-8 w-[150px] text-sm">
                      <SelectValue placeholder="Condition" />
                    </SelectTrigger>
                    <SelectContent>
                      {operators.map((op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {column?.type === "enum" ? (
                    <Select
                      value={rule.value || undefined}
                      onValueChange={(value) =>
                        updateRule(rule.id, { value })
                      }
                    >
                      <SelectTrigger className="h-8 w-[170px] text-sm">
                        <SelectValue placeholder="Value" />
                      </SelectTrigger>
                      <SelectContent>
                        {(column.options ?? []).map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      value={rule.value}
                      onChange={(event) =>
                        updateRule(rule.id, { value: event.target.value })
                      }
                      placeholder="Value"
                      inputMode={column?.type === "number" ? "decimal" : "text"}
                      className="h-8 w-[170px] text-sm"
                    />
                  )}

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove filter"
                    className="h-8 w-8 flex-none text-muted-foreground hover:text-foreground"
                    onClick={() => removeRule(rule.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="mt-2 rounded border border-dashed border-border bg-warm-bg p-3 text-sm text-muted-foreground">
            No filters yet. Add a condition to narrow the rows — filtering
            applies as you type.
          </div>
        )}

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-2 h-8 px-2 text-coral hover:text-coral-hover"
          onClick={addRule}
          disabled={!columns.length}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add filter
        </Button>
      </PopoverContent>
    </Popover>
  )
}
